import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogErrorBoundary, PostHogProvider } from "@posthog/react";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { isConsentRequiredMarket } from "./utility/geo";
import { getStoredConsent } from "./utility/consent";

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || "production",
    enabled: process.env.NODE_ENV === "production",
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
      // userInfo: false,
      // httpBodies: []
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.ngwaahia\.com/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true,
  });
}

if (process.env.REACT_APP_POSTHOG_API_KEY && process.env.NODE_ENV === "production") {
  const consentRequired = isConsentRequiredMarket();
  posthog.init(process.env.REACT_APP_POSTHOG_API_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2026-01-30",
    autocapture: false,
    // Explicit rather than relying on the `defaults` date's implicit
    // history-based pageview capture surviving future SDK upgrades.
    capture_pageview: "history_change",
    // UK/EEA visitors (see isConsentRequiredMarket) start opted out of both
    // capturing and persistence — other markets are unaffected (both false).
    opt_out_capturing_by_default: consentRequired,
    opt_out_persistence_by_default: consentRequired,
  });
  // Returning UK/EEA visitor who previously accepted — restore consent
  // silently (no repeat $opt_in event) rather than re-showing the banner.
  if (consentRequired && getStoredConsent() === "granted") {
    posthog.opt_in_capturing({ captureEventName: false });
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        <App />
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>,
);

reportWebVitals();
